import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";

import { Badge } from "@/components/ui/badge/Badge";

const BADGE_CLASS = (isLarge: boolean, isMinimized: boolean, className?: string) =>
  clsx(
    "max-w-full font-medium",
    isLarge ? "gap-2 rounded-lg px-3 py-1.5 text-sm" : isMinimized ? "gap-1 px-2 py-0.5 text-[11px]" : "gap-1.5",
    className,
  );

export function AgenticCaseProgressIndicator({
  label,
  size = "md",
  className,
  testId,
  title,
  onClick,
  variant = "running",
}: {
  label: string;
  size?: "sm" | "md" | "lg" | "minimized";
  className?: string;
  testId?: string;
  title?: string;
  onClick?: () => void;
  variant?: "running" | "blocked" | "idle";
}) {
  const isMinimized = size === "minimized";
  const isLarge = size === "lg";
  const badgeSize = isMinimized ? "sm" : isLarge ? "md" : size;
  const badgeColor = variant === "blocked" ? "amber" : "indigo";
  const showSpinner = variant === "running";

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title}
        aria-label={`${label}. View task progress.`}
        data-testid={testId}
        className={clsx(
          "inline-flex max-w-full cursor-pointer rounded-full border-0 bg-transparent p-0",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500",
          className,
        )}
      >
        <Badge
          variant="chip"
          color={badgeColor}
          size={badgeSize}
          className={clsx(BADGE_CLASS(isLarge, isMinimized), "pointer-events-none")}
        >
          {showSpinner ? (
            <FontAwesomeIcon
              icon={faSpinner}
              className={clsx(
                "shrink-0 animate-spin",
                isLarge ? "h-4 w-4" : isMinimized ? "h-3 w-3" : "h-3.5 w-3.5",
              )}
            />
          ) : null}
          <span className="truncate">{label}</span>
        </Badge>
      </button>
    );
  }

  return (
    <Badge
      variant="chip"
      color={badgeColor}
      size={badgeSize}
      className={BADGE_CLASS(isLarge, isMinimized, className)}
      data-testid={testId}
      title={title}
    >
      {showSpinner ? (
        <FontAwesomeIcon
          icon={faSpinner}
          className={clsx(
            "shrink-0 animate-spin",
            isLarge ? "h-4 w-4" : isMinimized ? "h-3 w-3" : "h-3.5 w-3.5",
          )}
        />
      ) : null}
      <span className="truncate">{label}</span>
    </Badge>
  );
}
